import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { delay, filter, finalize, map, switchMap, tap } from 'rxjs';
import { ApexYAxis, NgApexchartsModule } from 'ng-apexcharts';
import {
  DateRangePickerComponent,
  IntegratorSelectComponent,
  TrackerPositionFilters,
  WhApexThemeService,
} from '@wheere-front/shared-ui';
import { MultiSelect } from 'primeng/multiselect';
import dayjs from 'dayjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  GRAPH_MAX_RAW_POINTS,
  SharedWebSocketService,
  subscribeWhenReady,
  WebSocketConnectionType,
  graphReduceLabel,
  telemetryApexToolbar,
  telemetryApexZoom,
} from '@wheere-front/shared-util';
import { DataPoint, GraphData } from '@wheere/api-tracker';
import { TrackersV7Store } from '../data/trackers-v7.store';

interface DurationPreset {
  label: string;
  amount: number;
  unit: 'minute' | 'hour' | 'day' | 'month';
  reduce: number;
}

const DURATIONS: DurationPreset[] = [
  { label: '1m', amount: 1, unit: 'minute', reduce: 0 },
  { label: '5m', amount: 5, unit: 'minute', reduce: 0 },
  { label: '15m', amount: 15, unit: 'minute', reduce: 0 },
  { label: '1h', amount: 1, unit: 'hour', reduce: 500 },
  { label: '6h', amount: 6, unit: 'hour', reduce: 500 },
  { label: '1D', amount: 1, unit: 'day', reduce: 500 },
  { label: '1W', amount: 7, unit: 'day', reduce: 500 },
  { label: '1M', amount: 1, unit: 'month', reduce: 500 },
];

const REDUCE_VALUES = [0, 100, 250, 500, 1000, 2500, 5000];

const SERIES_COLORS = ['#ED107D', '#5000CD', '#23D2CD', '#107DED', '#FFC800', '#F05252'];

@Component({
  selector: 'lib-tracker-graphs',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    IntegratorSelectComponent,
    DateRangePickerComponent,
    NgApexchartsModule,
    MultiSelect,
    DatePipe,
    DecimalPipe,
    TranslocoModule,
  ],
  template: `
    <div class="flex flex-col gap-4">
      <div
        class="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-2 sm:gap-3 sm:p-3 dark:border-slate-700 dark:bg-slate-800">
        <div class="flex flex-wrap items-center gap-1">
          @for (d of durations; track d.label) {
            <button
              type="button"
              class="inline-flex h-8 items-center rounded-md border px-3 text-sm font-semibold transition-colors"
              [class.border-gray-300]="durationLabel() !== d.label"
              [class.text-gray-700]="durationLabel() !== d.label"
              [class.bg-white]="durationLabel() !== d.label"
              [class.hover:bg-gray-50]="durationLabel() !== d.label"
              [class.dark:border-slate-600]="durationLabel() !== d.label"
              [class.dark:bg-slate-700]="durationLabel() !== d.label"
              [class.dark:text-slate-200]="durationLabel() !== d.label"
              [class.dark:hover:bg-slate-600]="durationLabel() !== d.label"
              [class.bg-primary-500]="durationLabel() === d.label"
              [class.border-primary-500]="durationLabel() === d.label"
              [class.text-white]="durationLabel() === d.label"
              (click)="onDuration(d)">
              {{ d.label }}
            </button>
          }
        </div>

        <!-- Selects — locked to h-8 via the wh-h8-select styleClass below so they
             sit flush with the duration chips. PrimeNG's "small" size lands a few
             pixels taller depending on theme tokens; the ::ng-deep override pins
             both the closed control and its label to 2rem exact. -->
        <!-- Custom range: the shared date-range picker (same one as the position history), h-8
             like the duration chips. Applying it deselects the chips (durationLabel = 'custom');
             picking a chip takes over again. -->
        <lib-date-range-picker
          [initialFilters]="rangeFilters()"
          [hideStatusFilter]="true"
          (filtersApplied)="onCustomRange($event)" />

        @if (graphData().length > 0) {
          <p-multiselect
            class="wh-h8-select"
            [options]="graphData()"
            [ngModel]="selectedGraphTitles()"
            (ngModelChange)="selectedGraphTitles.set($event)"
            optionLabel="title"
            optionValue="title"
            [placeholder]="'trackerGraphs.selectGraphs' | transloco"
            appendTo="body"
            size="small"
            [style]="{ minWidth: '14rem', maxWidth: '20rem', height: '2rem' }">
          </p-multiselect>
        }

        <lib-integrator-select
          width="10rem"
          size="sm"
          accent="primary"
          [options]="reduceOptions()"
          [(ngModel)]="reduce" />

        <span class="ml-auto flex items-center gap-3 text-xs text-gray-500 dark:text-slate-400">
          <span class="flex items-center gap-1.5">
            <span
              class="h-2 w-2 rounded-full"
              [class.bg-green-500]="wsConnected()"
              [class.bg-gray-400]="!wsConnected()"></span>
            {{ (wsConnected() ? 'trackerGraphs.live' : 'trackerGraphs.offline') | transloco }}
          </span>
          @switch (reduceLabel()) {
            @case ('reduced') {
              <span>{{ 'trackerGraphs.pctDisplayed' | transloco: { pct: numberOfPointDisplay() | number } }}</span>
            }
            @case ('rawCapped') {
              <span>{{
                'trackerGraphs.rawCapped'
                  | transloco: { max: maxRawPoints | number, pct: numberOfPointDisplay() | number }
              }}</span>
            }
          }
          <span>{{ formatRange() }}</span>
        </span>
      </div>

      @if (loading()) {
        <div
          class="flex h-80 items-center justify-center rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          <span class="animate-pulse text-gray-400 dark:text-slate-500">{{ 'trackerGraphs.loading' | transloco }}</span>
        </div>
      } @else if (filteredGraphs().length === 0) {
        <div
          class="flex h-80 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white text-gray-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:text-slate-500">
          {{ 'trackerGraphs.noData' | transloco }}
        </div>
      } @else {
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          @for (graph of filteredGraphs(); track graph.title) {
            <div class="rounded-xl border border-gray-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
              <div class="mb-2 flex items-center justify-between">
                <div
                  class="wh-display truncate text-sm font-semibold text-gray-900 dark:text-slate-100"
                  [title]="graph.title">
                  {{ graph.title }}
                </div>
                <div class="flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400">
                  <span>{{ 'trackerGraphs.yAxis.min' | transloco }}</span>
                  <input
                    type="number"
                    [placeholder]="'trackerGraphs.yAxis.auto' | transloco"
                    [value]="getYAxis(graph.title!).min"
                    (change)="setYAxisMin(graph.title!, $any($event.target).value)"
                    class="focus:border-primary-500 w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-900 focus:outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100" />
                  <span>{{ 'trackerGraphs.yAxis.max' | transloco }}</span>
                  <input
                    type="number"
                    [placeholder]="'trackerGraphs.yAxis.auto' | transloco"
                    [value]="getYAxis(graph.title!).max"
                    (change)="setYAxisMax(graph.title!, $any($event.target).value)"
                    class="focus:border-primary-500 w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-900 focus:outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100" />
                </div>
              </div>

              <div class="mb-1 flex flex-wrap justify-center gap-3 text-xs text-gray-500 dark:text-slate-400">
                @for (legend of graph.legends ?? []; track legend; let i = $index) {
                  <span class="flex items-center gap-1.5">
                    <span class="h-2 w-2 rounded-full" [style.background]="colorFor(i)"></span>
                    <span>{{ legend }}</span>
                    <span
                      class="wh-display text-gray-900 dark:text-slate-100"
                      [title]="lastTimestamp(graph) | date: 'dd/MM HH:mm:ss'">
                      {{ lastValue(graph, legend) }}
                    </span>
                  </span>
                }
              </div>

              <apx-chart
                [series]="seriesFor(graph)"
                [chart]="chartOptions"
                [xaxis]="xaxisOptions"
                [yaxis]="yAxisFor(graph)"
                [stroke]="strokeOptions"
                [colors]="seriesColors"
                [legend]="legendOptions"
                [dataLabels]="dataLabelsOptions"
                [theme]="apex.theme()"
                [tooltip]="apex.tooltip()"
                [grid]="apex.grid()"
                [markers]="markersOptions">
              </apx-chart>
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      /* Pin PrimeNG p-select / p-multiselect to h-8 (2rem) so they line up
         with the duration chips. We hit:
           1. the host element (where wh-h8-select lives + maybe .p-select)
           2. any descendant .p-select / .p-multiselect (older PrimeNG layouts)
           3. the input inside (.p-multiselect-input is the focusable wrapper)
           4. the visible label (where the chevron + text live)
         All forced with !important + display:flex so the internal padding
         tokens Aura sets via CSS vars don't push the height past 2rem. */
      :host ::ng-deep .wh-h8-select,
      :host ::ng-deep .wh-h8-select.p-select,
      :host ::ng-deep .wh-h8-select.p-multiselect,
      :host ::ng-deep .wh-h8-select .p-select,
      :host ::ng-deep .wh-h8-select .p-multiselect,
      :host ::ng-deep .wh-h8-select .p-multiselect-input {
        height: 2rem !important;
        min-height: 2rem !important;
        max-height: 2rem !important;
        display: flex !important;
        align-items: center !important;
        box-sizing: border-box !important;
      }
      :host ::ng-deep .wh-h8-select .p-select-label,
      :host ::ng-deep .wh-h8-select .p-multiselect-label,
      :host ::ng-deep .wh-h8-select .p-multiselect-label-container {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        line-height: 1.5rem !important;
        height: 100% !important;
        display: inline-flex !important;
        align-items: center !important;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      /* Override the design-token padding Aura sets via --p-* CSS variables.
         Without this, the y-padding token kicks the form field back up to
         ~2.5rem regardless of any height we set. */
      :host ::ng-deep .wh-h8-select {
        --p-form-field-padding-y: 0 !important;
        --p-form-field-sm-padding-y: 0 !important;
      }
    `,
  ],
})
export class TrackerGraphsComponent implements OnInit, OnDestroy {
  /**
   * Every apx-chart input other than `series` must keep a stable reference: ng-apexcharts
   * destroys and re-creates the chart when any of them changes (`hydrate()`), and inline object
   * literals in the template change on every change detection — one per live frame. That is
   * what closed the download menu the instant it opened, and redrew every chart every second.
   * Series and y-axis are memoised per graph object / per y-range.
   */
  protected readonly chartOptions = {
    type: 'line' as const,
    height: 200,
    background: 'transparent',
    animations: { enabled: false },
    toolbar: telemetryApexToolbar,
    zoom: telemetryApexZoom,
  };
  protected readonly xaxisOptions = {
    type: 'datetime' as const,
    labels: { style: { fontSize: '10px' }, datetimeUTC: false },
  };
  protected readonly strokeOptions = { curve: 'smooth' as const, width: 2 };
  protected readonly legendOptions = { show: false };
  protected readonly dataLabelsOptions = { enabled: false };
  protected readonly markersOptions = { size: 0 };
  readonly #seriesCache = new WeakMap<GraphData, ReturnType<typeof this.buildSeries>>();
  readonly #yAxisCache = new Map<string, ApexYAxis>();

  protected seriesFor(graph: GraphData): ReturnType<typeof this.buildSeries> {
    let s = this.#seriesCache.get(graph);
    if (!s) {
      s = this.buildSeries(graph);
      this.#seriesCache.set(graph, s);
    }
    return s;
  }

  protected yAxisFor(graph: GraphData): ApexYAxis {
    const cfg = this.getYAxis(graph.title!);
    const key = `${graph.title}|${cfg.min}|${cfg.max}`;
    let y = this.#yAxisCache.get(key);
    if (!y) {
      y = this.buildYAxis(graph);
      this.#yAxisCache.set(key, y);
    }
    return y;
  }
  trackerId = input.required<string>();
  /**
   * STOMP connection the live graph subscribes on. The integrator opens `INTEGRATOR`; inside
   * opens its own per-domain connections and must pass one of them, or live never subscribes.
   */
  wsConnection = input<WebSocketConnectionType>(WebSocketConnectionType.INTEGRATOR);

  protected readonly durations = DURATIONS;
  protected readonly seriesColors = SERIES_COLORS;

  protected readonly graphData = signal<GraphData[]>([]);
  protected readonly loading = signal(false);
  protected readonly numberOfPointDisplay = signal<number>(100);

  protected readonly durationLabel = signal<string>('1h');
  /** Seed of the custom range picker — follows whatever range is currently displayed. */
  protected readonly rangeFilters = computed<TrackerPositionFilters>(() => ({
    from: this.startDate(),
    to: this.endDate(),
  }));
  protected readonly startDate = signal<Date>(dayjs().subtract(1, 'hour').toDate());
  protected readonly endDate = signal<Date>(new Date());
  protected readonly reduce = signal<number>(500);
  protected readonly maxRawPoints = GRAPH_MAX_RAW_POINTS;
  protected readonly reduceLabel = computed(() => graphReduceLabel(this.reduce(), this.numberOfPointDisplay()));

  // Signal (not a plain array): filteredGraphs is a computed, so the selection
  // must be a tracked dependency — with a plain property the checkboxes in the
  // multiselect had no effect on the rendered graphs.
  selectedGraphTitles = signal<string[]>([]);

  protected readonly yAxisConfigs = signal<Record<string, { min: number | null; max: number | null }>>({});
  protected readonly wsConnected = signal(false);

  protected readonly filteredGraphs = computed<GraphData[]>(() => {
    const all = this.graphData();
    const selected = this.selectedGraphTitles();
    if (!selected.length) return all;
    const set = new Set(selected);
    return all.filter(g => set.has(g.title!));
  });

  protected readonly apex = inject(WhApexThemeService);

  #store = inject(TrackersV7Store);
  #ws = inject(SharedWebSocketService);
  #injector = inject(Injector);
  #transloco = inject(TranslocoService);

  /** Re-derive the reduce-option labels whenever the active language changes
   * so the "No reduction (raw)" / "{{n}} pts" labels stay localised. */
  private readonly activeLang = toSignal(this.#transloco.langChanges$, {
    initialValue: this.#transloco.getActiveLang(),
  });
  protected readonly reduceOptions = computed(() => {
    this.activeLang(); // dependency
    return REDUCE_VALUES.map(n => ({
      label:
        n === 0
          ? this.#transloco.translate('trackerGraphs.reduce.raw')
          : this.#transloco.translate('trackerGraphs.reduce.pts', { n }),
      value: n,
    }));
  });

  #stopLiveGraph: (() => void) | null = null;

  constructor() {
    effect(
      () => {
        const list = this.graphData();
        if (list.length && !this.selectedGraphTitles().length) {
          this.selectedGraphTitles.set(list.map(g => g.title!));
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const params = computed(() => ({
        id: this.trackerId(),
        start: this.startDate(),
        end: this.endDate(),
        reduce: this.reduce(),
      }));
      toObservable(params)
        .pipe(
          filter(({ id, start, end }) => !!id && !!start && !!end),
          tap(() => this.loading.set(true)),
          switchMap(({ id, start, end, reduce }) => {
            return new Promise<{ graphs: GraphData[]; pct: number }>(resolve => {
              this.#store
                .loadTrackerGraphs(id, start, end, reduce)
                .then(resp => {
                  const graphs = (resp.graphDatas ?? []).slice().sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
                  resolve({ graphs, pct: resp.reducePourcentage ?? 100 });
                })
                .catch(() => resolve({ graphs: [], pct: 0 }));
            });
          }),
          delay(150),
          tap(({ graphs, pct }) => {
            this.graphData.set(graphs);
            this.numberOfPointDisplay.set(pct);
          }),
          finalize(() => this.loading.set(false)),
          takeUntilDestroyed(),
          map(() => undefined)
        )
        .subscribe({
          next: () => this.loading.set(false),
          error: () => this.loading.set(false),
        });
    });

    this.subscribeToLiveGraph();
  }

  ngOnDestroy(): void {
    this.unsubscribeFromLiveGraph();
  }

  /** Custom range from the picker: no chip stays selected, the reduce factor is kept. */
  protected onCustomRange(f: TrackerPositionFilters): void {
    if (!f.from || !f.to) return;
    this.durationLabel.set('custom');
    this.startDate.set(f.from);
    this.endDate.set(f.to);
  }

  protected onDuration(d: DurationPreset): void {
    this.durationLabel.set(d.label);
    this.startDate.set(dayjs().subtract(d.amount, d.unit).toDate());
    this.endDate.set(new Date());
    this.reduce.set(d.reduce);
  }

  protected colorFor(i: number): string {
    return SERIES_COLORS[i % SERIES_COLORS.length];
  }

  protected buildSeries(graph: GraphData): { name: string; data: { x: number; y: number | null }[] }[] {
    return (graph.legends ?? []).map(legend => ({
      name: legend,
      data: (graph.data ?? []).map((dp: DataPoint) => ({
        x: new Date(dp.timestamp as unknown as string).getTime(),
        y: (dp.values?.[legend] as number | undefined) ?? null,
      })),
    }));
  }

  protected getYAxis(title: string): { min: number | null; max: number | null } {
    return this.yAxisConfigs()[title] ?? { min: null, max: null };
  }

  protected setYAxisMin(title: string, value: string): void {
    const next = { ...this.yAxisConfigs() };
    next[title] = { ...this.getYAxis(title), min: value === '' ? null : Number(value) };
    this.yAxisConfigs.set(next);
  }

  protected setYAxisMax(title: string, value: string): void {
    const next = { ...this.yAxisConfigs() };
    next[title] = { ...this.getYAxis(title), max: value === '' ? null : Number(value) };
    this.yAxisConfigs.set(next);
  }

  protected buildYAxis(graph: GraphData): ApexYAxis {
    const cfg = this.getYAxis(graph.title!);
    const yaxis: ApexYAxis = { labels: { style: { fontSize: '10px' } } };
    if (cfg.min !== null) yaxis.min = cfg.min;
    if (cfg.max !== null) yaxis.max = cfg.max;
    return yaxis;
  }

  protected lastValue(graph: GraphData, legend: string): string {
    const dp = (graph.data ?? []).at(-1);
    const v = dp?.values?.[legend];
    if (v == null) return '—';
    return typeof v === 'number' ? this.formatNumber(v) : String(v);
  }

  protected lastTimestamp(graph: GraphData): Date | null {
    const dp = (graph.data ?? []).at(-1);
    return dp?.timestamp ? new Date(dp.timestamp as unknown as string) : null;
  }

  protected formatRange(): string {
    return `${dayjs(this.startDate()).format('DD/MM HH:mm')} → ${dayjs(this.endDate()).format('DD/MM HH:mm')}`;
  }

  private formatNumber(v: number): string {
    if (Math.abs(v) >= 1000 || Number.isInteger(v)) return v.toFixed(0);
    if (Math.abs(v) >= 1) return v.toFixed(2);
    return v.toFixed(4);
  }

  private subscribeToLiveGraph(): void {
    this.unsubscribeFromLiveGraph();
    this.#stopLiveGraph = subscribeWhenReady(
      this.#ws,
      this.wsConnection(),
      `/topic/tracker/${this.trackerId()}/graph`,
      raw => {
        const updated = this.parsePayload<GraphData>(raw);
        if (!updated) return;
        this.appendLiveGraph(updated);
      },
      { onReadyChange: ready => this.wsConnected.set(ready) }
    );
  }

  private unsubscribeFromLiveGraph(): void {
    this.#stopLiveGraph?.();
    this.#stopLiveGraph = null;
  }

  private parsePayload<T>(raw: unknown): T | null {
    if (raw == null) return null;
    if (typeof raw !== 'string') return raw as T;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  private appendLiveGraph(updated: GraphData): void {
    const current = this.graphData();
    const existing = current.find(g => g.title === updated.title);
    if (existing) {
      const merged: GraphData[] = current.map(g =>
        g.title === updated.title ? { ...g, data: [...(g.data ?? []), ...(updated.data ?? [])] } : g
      );
      this.graphData.set(merged);
    } else {
      this.graphData.set([...current, updated]);
      if (!this.selectedGraphTitles().includes(updated.title!)) {
        this.selectedGraphTitles.update(titles => [...titles, updated.title!]);
      }
    }
  }
}
